/**
 * customsirge_marquee.js — seamless auto-scrolling rows.
 *
 * Markup contract:
 *   <root data-customsirge-marquee>
 *     <viewport (overflow:hidden)>
 *       <track data-customsirge-marquee-track>
 *         <row data-customsirge-marquee-row>…items…</row>
 *         <row data-customsirge-marquee-clone aria-hidden="true">…items…</row>  (static no-JS copy)
 *
 * A pure-CSS two-copy marquee only looks full when a single copy is already wider
 * than the viewport — otherwise the row scrolls in leaving dead space beside it.
 * Copy count is therefore viewport-dependent and has to be measured: this clones the
 * row until the track spans at least the viewport plus one full cycle, then hands the
 * cycle width to CSS as --customsirge-marquee-cycle so the keyframes translate by
 * exactly one row and loop seamlessly from a full first frame.
 */
(function () {
  if (window.customsirgeMarquee) return;

  var PX_PER_SECOND = 30; // scroll speed, independent of how many copies exist
  var MIN_DURATION = 8;

  function prefersReducedMotion() {
    return (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function stripEditorAttributes(node) {
    // Clones must not carry theme-editor block ids or duplicate element ids.
    var all = [node].concat(Array.prototype.slice.call(node.querySelectorAll('*')));
    all.forEach(function (el) {
      if (el.id) el.removeAttribute('id');
      Array.prototype.slice.call(el.attributes).forEach(function (attr) {
        if (attr.name.indexOf('data-shopify') === 0) el.removeAttribute(attr.name);
      });
    });
  }

  function layout(root) {
    var track = root.querySelector('[data-customsirge-marquee-track]');
    if (!track) return;
    var row = track.querySelector('[data-customsirge-marquee-row]');
    if (!row) return;

    // Always rebuild from the single source row so resizes don't accumulate copies.
    track.querySelectorAll('[data-customsirge-marquee-clone]').forEach(function (clone) {
      clone.remove();
    });

    if (prefersReducedMotion()) {
      track.style.removeProperty('--customsirge-marquee-cycle');
      track.style.removeProperty('animation-duration');
      return;
    }

    var cycle = row.getBoundingClientRect().width;
    var viewport = root.getBoundingClientRect().width;
    if (!cycle || !viewport) return;

    // The track must still cover the viewport at the end of the travel, i.e. after it
    // has moved left by one cycle. floor + 2 keeps a strict one-row coverage margin even
    // when viewport/cycle is an exact integer (ceil + 1 would only just touch the edge).
    var copies = Math.floor(viewport / cycle) + 2;
    if (copies < 2) copies = 2;

    var frag = document.createDocumentFragment();
    for (var i = 1; i < copies; i++) {
      var clone = row.cloneNode(true);
      clone.removeAttribute('data-customsirge-marquee-row');
      clone.setAttribute('data-customsirge-marquee-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      stripEditorAttributes(clone);
      frag.appendChild(clone);
    }
    track.appendChild(frag);

    track.style.setProperty('--customsirge-marquee-cycle', cycle + 'px');
    track.style.animationDuration =
      Math.max(MIN_DURATION, cycle / PX_PER_SECOND) + 's';

    // Restart the CSS animation so it re-resolves its keyframes against the cycle we just set.
    // The animation started at render using the `50%` fallback against the two server-rendered
    // copies; without this restart it keeps translating by 50% of the now-widened track (many
    // rows) instead of one row, overshooting into white space and a visible jump. A fresh
    // animation instance resolves keyframe values from current computed style, so the end value
    // becomes exactly -1 × one row = seamless. Name-agnostic (USP vs. trust keyframes) and it
    // preserves the inline animation-duration set above by touching only animation-name.
    var name = getComputedStyle(track).animationName;
    if (name && name !== 'none') {
      track.style.animationName = 'none';
      void track.offsetWidth; // reflow so the restart takes effect
      track.style.animationName = name;
    }
  }

  var roots = [];

  function layoutAll() {
    roots.forEach(layout);
  }

  var resizeTimer = null;
  function onResize() {
    if (resizeTimer) clearTimeout(resizeTimer);
    resizeTimer = setTimeout(layoutAll, 150);
  }

  function observeRow(root) {
    if (typeof ResizeObserver !== 'function') return;
    var row = root.querySelector('[data-customsirge-marquee-row]');
    if (!row) return;
    // Late-loading icons/fonts change the row width after the first measurement.
    // Cloning never resizes the source row, so this cannot feed back on itself.
    var ro = new ResizeObserver(function () {
      layout(root);
    });
    ro.observe(row);
  }

  function collect() {
    roots = Array.prototype.slice.call(
      document.querySelectorAll('[data-customsirge-marquee]')
    );
    roots.forEach(observeRow);
  }

  function init() {
    collect();
    layoutAll();
    window.addEventListener('resize', onResize);
    window.addEventListener('load', layoutAll);
  }

  window.customsirgeMarquee = { layout: layoutAll };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor: sections are re-rendered on setting changes.
  document.addEventListener('shopify:section:load', function () {
    collect();
    layoutAll();
  });
})();
